import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { X, FileText, FileUp, Loader2, Save } from "lucide-react";
import type { LibrarySource, SubSession } from "@/content/library";
import { useLibraryContentStore, newSubId } from "@/store/libraryContentStore";
import { useCategories } from "@/store/customContentStore";
import { readMarkdownFile, titleFromMarkdown, extractPdfText, fileBaseName } from "@/core/import";
import RichTextEditor from "@/components/RichTextEditor";

interface LibraryEditorProps {
  sub: SubSession | null; // null = new sub-session
  defaultMainId?: string;
  onClose: () => void;
}

function sourcesToText(sources: LibrarySource[]): string {
  return sources.map((s) => (s.author ? `${s.title} — ${s.author}` : s.title)).join("\n");
}
function parseSources(text: string): LibrarySource[] {
  return text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const [title, author] = l.split(" — ");
      return author ? { title: title.trim(), author: author.trim() } : { title: title.trim() };
    });
}

export default function LibraryEditor({ sub, defaultMainId, onClose }: LibraryEditorProps) {
  const saveSub = useLibraryContentStore((s) => s.saveSub);
  const mains = useLibraryContentStore((s) => s.orderedMains());
  const CATEGORIES = useCategories();
  const isNew = !sub;

  const [title, setTitle] = useState(sub?.title ?? "");
  const [mainId, setMainId] = useState(sub?.mainId ?? defaultMainId ?? mains[0]?.id ?? "");
  const [disciplineId, setDisciplineId] = useState(sub?.disciplineId ?? "");
  const [tags, setTags] = useState((sub?.tags ?? []).join(", "));
  const [body, setBody] = useState(sub?.body ?? "");
  const [sourcesText, setSourcesText] = useState(sub ? sourcesToText(sub.sources) : "");
  const [importing, setImporting] = useState<null | "md" | "pdf">(null);
  const [error, setError] = useState("");

  const mdInput = useRef<HTMLInputElement>(null);
  const pdfInput = useRef<HTMLInputElement>(null);

  async function onMarkdown(file: File) {
    setError("");
    setImporting("md");
    try {
      const md = await readMarkdownFile(file);
      setBody(md);
      if (!title.trim()) setTitle(titleFromMarkdown(md, fileBaseName(file.name)));
    } catch {
      setError("Couldn't read that Markdown file.");
    } finally {
      setImporting(null);
    }
  }

  async function onPdf(file: File) {
    setError("");
    setImporting("pdf");
    try {
      const text = await extractPdfText(file);
      setBody((b) => (b.trim() ? `${b}\n\n${text}` : text));
      if (!title.trim()) setTitle(fileBaseName(file.name));
      if (!text) setError("No selectable text found in that PDF (it may be scanned images).");
    } catch {
      setError("Couldn't extract text from that PDF.");
    } finally {
      setImporting(null);
    }
  }

  function save() {
    if (!title.trim() || !body.trim()) {
      setError("A title and some content are required.");
      return;
    }
    if (!mainId) {
      setError("Pick a main session (create one first).");
      return;
    }
    const built: SubSession = {
      id: sub?.id ?? newSubId(),
      mainId,
      title: title.trim(),
      body: body.trim(),
      disciplineId: disciplineId || undefined,
      sources: parseSources(sourcesText),
      tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      order: sub?.order ?? 0,
    };
    saveSub(built);
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        className="relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h3 className="font-display text-lg font-semibold text-text">{isNew ? "Add a sub-session" : "Edit sub-session"}</h3>
          <button onClick={onClose} aria-label="Close" className="grid h-8 w-8 place-items-center rounded-lg border border-border text-muted hover:bg-surface-2 hover:text-text">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {/* Import */}
          <div className="flex flex-wrap gap-2">
            <button onClick={() => mdInput.current?.click()} disabled={!!importing} className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface-2 px-3 py-2 text-sm font-medium text-text transition-colors hover:bg-border/40 disabled:opacity-50">
              {importing === "md" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Import Markdown
            </button>
            <button onClick={() => pdfInput.current?.click()} disabled={!!importing} className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface-2 px-3 py-2 text-sm font-medium text-text transition-colors hover:bg-border/40 disabled:opacity-50">
              {importing === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />} Import PDF
            </button>
            <input ref={mdInput} type="file" accept=".md,.markdown,.txt,text/markdown,text/plain" hidden onChange={(e) => e.target.files?.[0] && onMarkdown(e.target.files[0])} />
            <input ref={pdfInput} type="file" accept="application/pdf,.pdf" hidden onChange={(e) => e.target.files?.[0] && onPdf(e.target.files[0])} />
          </div>

          <Field label="Title">
            <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} placeholder="Sub-session title" />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Main session">
              <select value={mainId} onChange={(e) => setMainId(e.target.value)} className={inputCls}>
                {mains.map((m) => (
                  <option key={m.id} value={m.id}>{m.title}</option>
                ))}
              </select>
            </Field>
            <Field label="Practice link (discipline — optional)">
              <select value={disciplineId} onChange={(e) => setDisciplineId(e.target.value)} className={inputCls}>
                <option value="">None</option>
                {CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>{c.group} · {c.name}</option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Tags (comma-separated)">
            <input value={tags} onChange={(e) => setTags(e.target.value)} className={inputCls} placeholder="core-loop, theory" />
          </Field>

          <Field label="Content">
            <RichTextEditor value={body} onChange={setBody} placeholder="Write or import your notes… Markdown shortcuts work (# heading, - list, > quote)." minHeight="12rem" />
          </Field>

          <Field label="Sources (one per line: Title — Author)">
            <textarea value={sourcesText} onChange={(e) => setSourcesText(e.target.value)} className={`${inputCls} h-20 resize-y`} placeholder="The Art of Game Design — Jesse Schell" />
          </Field>

          {error && <p className="text-sm text-amber-400">{error}</p>}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <button onClick={onClose} className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted hover:text-text">Cancel</button>
          <button onClick={save} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-fg shadow-glow transition-transform hover:scale-[1.02]">
            <Save className="h-4 w-4" /> Save
          </button>
        </div>
      </motion.div>
    </div>
  );
}

const inputCls =
  "w-full rounded-lg border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted">{label}</span>
      {children}
    </label>
  );
}
